// Budget Builder Component
// Generate plan items based on budget and career stage mix

import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { DollarSign, Sparkles, Users as UsersIcon } from 'lucide-react';
import { MaxSpinner } from "@/components/ui/max-spinner";
import { cn } from '@/lib/utils';

interface BudgetBuilderProps {
  careerStages: string[];
  onGenerate: (params: {
    totalBudget: number;
    selectedStages: string[];
    distribution: string;
    packageTier: 'set-core' | 'set-audio' | 'set-video';
    strictMode: boolean;
    targetShowsPerArtist: number;
  }) => Promise<void>;
  isGenerating: boolean;
}

export function BudgetBuilder({ careerStages, onGenerate, isGenerating }: BudgetBuilderProps) {
  const [totalBudget, setTotalBudget] = useState<number>(50000);
  const [selectedCareerStages, setSelectedCareerStages] = useState<string[]>([]);
  const [budgetDistribution, setBudgetDistribution] = useState<string>('even');
  const [defaultPackage, setDefaultPackage] = useState<'set-core' | 'set-audio' | 'set-video'>('set-audio');
  const [targetShowsPerArtist, setTargetShowsPerArtist] = useState<number>(5);
  const [strictMode, setStrictMode] = useState(false);

  const toggleCareerStage = (stage: string) => {
    setSelectedCareerStages(prev =>
      prev.includes(stage)
        ? prev.filter(s => s !== stage)
        : [...prev, stage]
    );
  };

  const handleGenerate = async () => {
    if (selectedCareerStages.length === 0) {
      return;
    }

    await onGenerate({
      totalBudget,
      selectedStages: selectedCareerStages,
      distribution: budgetDistribution,
      packageTier: defaultPackage,
      strictMode,
      targetShowsPerArtist,
    });
  };

  return (
    <div className="space-y-4 flex-1">
      {/* Total Budget */}
      <div className="space-y-2">
        <Label htmlFor="totalBudget" className="flex items-center gap-2 text-base font-semibold">
          <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center">
            <DollarSign className="h-4 w-4 text-green-600" />
          </div>
          Total Budget
        </Label>
        <div className="relative">
          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 font-medium">$</span>
          <Input
            id="totalBudget"
            type="number"
            min={1000}
            step={1000}
            value={totalBudget}
            onChange={(e) => setTotalBudget(parseInt(e.target.value) || 50000)}
            className="h-11 pl-7 border-green-200 focus:border-green-400 focus:ring-green-400 text-base"
          />
        </div>
      </div>

      {/* Select Career Stages */}
      <div className="space-y-2">
        <Label className="flex items-center gap-2 text-base font-semibold">
          <div className="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center">
            <Sparkles className="h-4 w-4 text-purple-600" />
          </div>
          Select Career Stages
        </Label>
        <div className="border border-purple-200 rounded-lg p-3 max-h-48 overflow-y-auto bg-white">
          <div className="space-y-2">
            {careerStages.map((stage) => (
              <div key={stage} className="flex items-center space-x-2">
                <Checkbox
                  id={`stage-${stage}`}
                  checked={selectedCareerStages.includes(stage)}
                  onCheckedChange={() => toggleCareerStage(stage)}
                />
                <label
                  htmlFor={`stage-${stage}`}
                  className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer"
                >
                  {stage}
                </label>
              </div>
            ))}
          </div>
        </div>
        <p className="text-xs text-gray-500">
          {selectedCareerStages.length === 0
            ? 'Select at least one career stage'
            : `${selectedCareerStages.length} stage${selectedCareerStages.length > 1 ? 's' : ''} selected`
          }
        </p>
      </div>

      {/* Budget Distribution */}
      <div className="space-y-2">
        <Label htmlFor="budgetDistribution" className="text-base font-semibold">
          Budget Distribution
        </Label>
        <Select
          value={budgetDistribution}
          onValueChange={setBudgetDistribution}
          disabled={selectedCareerStages.length < 2}
        >
          <SelectTrigger className="h-11 border-purple-200 focus:border-purple-400 focus:ring-purple-400">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="even">Even Split (Equal %)</SelectItem>
            <SelectItem value="favor-lower">Favor Lower (40→30→20→10%)</SelectItem>
            <SelectItem value="favor-higher">Favor Higher (10→20→30→40%)</SelectItem>
            <SelectItem value="heavy-lower">Heavy Lower (50→30→15→5%)</SelectItem>
            <SelectItem value="heavy-higher">Heavy Higher (5→15→30→50%)</SelectItem>
          </SelectContent>
        </Select>
        {selectedCareerStages.length < 2 && (
          <p className="text-xs text-gray-500">Select 2+ stages to choose distribution</p>
        )}
      </div>

      {/* Default Package */}
      <div className="space-y-2">
        <Label htmlFor="defaultPackage" className="text-base font-semibold">
          Default Package
        </Label>
        <Select
          value={defaultPackage}
          onValueChange={(val) => setDefaultPackage(val as 'set-core' | 'set-audio' | 'set-video')}
        >
          <SelectTrigger className="h-11 border-blue-200 focus:border-blue-400 focus:ring-blue-400">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="set-core">SET Core</SelectItem>
            <SelectItem value="set-audio">SET Audio</SelectItem>
            <SelectItem value="set-video">SET Video</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Target Shows per Artist */}
      <div className="space-y-2">
        <Label htmlFor="targetShowsPerArtist" className="flex items-center gap-2 text-base font-semibold">
          <div className="w-8 h-8 bg-orange-100 rounded-lg flex items-center justify-center">
            <UsersIcon className="h-4 w-4 text-orange-600" />
          </div>
          Shows per Artist
        </Label>
        <Select
          value={targetShowsPerArtist.toString()}
          onValueChange={(val) => setTargetShowsPerArtist(parseInt(val))}
        >
          <SelectTrigger className="h-11 border-orange-200 focus:border-orange-400 focus:ring-orange-400">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="1">1 show (maximize diversity)</SelectItem>
            <SelectItem value="3">3 shows (moderate grouping)</SelectItem>
            <SelectItem value="5">5 shows (recommended)</SelectItem>
            <SelectItem value="10">10 shows (aggressive grouping)</SelectItem>
          </SelectContent>
        </Select>
        <p className="text-xs text-gray-500">
          Target number of shows per artist for budget optimization
        </p>
      </div>

      {/* Strict Mode */}
      <div className="flex items-center space-x-2 pt-2">
        <Checkbox
          id="strictMode"
          checked={strictMode}
          onCheckedChange={(checked) => setStrictMode(checked as boolean)}
        />
        <label
          htmlFor="strictMode"
          className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer"
        >
          Strict mode (never exceed budget)
        </label>
      </div>

      {/* Generate Button */}
      <div className="pt-2">
        <Button
          onClick={handleGenerate}
          disabled={isGenerating || selectedCareerStages.length === 0}
          className="w-full h-12 text-base bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700"
        >
          {isGenerating ? (
            <>
              <MaxSpinner className="mr-2 h-5 w-5" />
              Generating Plan...
            </>
          ) : (
            <>
              <Sparkles className="mr-2 h-5 w-5" />
              Generate Plan
            </>
          )}
        </Button>
      </div>
    </div>
  );
}
